import type * as Api from '@ViewModels';

/**
 * Resolves a display name for a conversation's creator (`UserReference`). Prefers the combined
 * first/last name, then `fullName`, then the creator id, finally falling back to an em dash.
 */
export const getCreatorLabel = (creator?: Api.UserReference): string => {
	if (!creator) {
		return '—';
	}

	const fullName = [creator.firstName, creator.lastName].filter(Boolean).join(' ').trim();
	if (fullName) {
		return fullName;
	}

	if (creator.fullName?.trim()) {
		return creator.fullName.trim();
	}

	return creator.id ?? '—';
};

/**
 * Resolves up to two uppercase initials for a conversation's creator, for the metadata avatar.
 * Derives from the resolved display name (`getCreatorLabel`): the first letter of the first two
 * words, or a single letter when only one word is available. Falls back to "?" when no name or id
 * is known (so the avatar is never blank).
 */
export const getCreatorInitials = (creator?: Api.UserReference): string => {
	const label = getCreatorLabel(creator);
	if (label === '—') {
		return '?';
	}

	const words = label.split(/\s+/).filter(Boolean);
	const initials = words
		.slice(0, 2)
		.map(word => word[0])
		.join('');

	return initials.toUpperCase() || '?';
};
